import {
    Divider,
    Flex,
    HStack,
    Image,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Text,
    useDisclosure,
    useTheme,
} from '@chakra-ui/react';
import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useState } from 'react';
import {
    updateFiles,
    updateTags,
} from '../../../../canopy-ui-components/api/documents';
import CanopyButton from '../../../../canopy-ui-components/components/CanopyButton/CanopyButton';
import CanopyIconButton from '../../../../canopy-ui-components/components/IconButton/IconButton';
import * as ga from '../../../../canopy-ui-components/google-analytics';
import { useCurrentColors } from '../../../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';

import EditFileDetails from './EditFileDetails';

function EditFileDetailsModal({
    userId,
    data,
    showModal,
    setShowModal,
    globalProps,
    refreshData,
}: {
    userId?: number[];
    data?: any[];
    showModal: boolean;
    setShowModal: (flag: boolean) => void;
    globalProps?: GlobalProps;
    refreshData?: (args?: any) => void;
}) {
    const currentColors = useCurrentColors();
    const { t } = globalProps || {};
    const [isLoading, setIsLoading] = useState(false);
    const [inputFiles, setInputFiles] = useState<Record<string, any>[]>([]);
    const [isFinalState, setIsFinalState] = useState(false);
    const [isDisable, setIsDisable] = useState(true);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const theme = useTheme();

    const reAssignAdditionalInfo = (data: any) => {
        const inputFiles = [...(data || [])]?.map((file) => {
            return {
                documentId: file.documentId,
                name: file.fileName,
                key: file.filePath,
                size: '',
                description: file?.description || '',
                password: file.filePassword || '',
                status: 'ready-to-update',
                message: '',
                transactionTags:
                    file?.transactionTagValue?.map(
                        (item: any) => item.tagValue
                    ) || [],
                securityTags:
                    file?.securityTagValue?.map((item: any) => item.tagValue) ||
                    [],
                customTags:
                    file?.customTagValue?.map((item: any) => item.tagValue) ||
                    [],
                keyBoardInputMode: false,
                enabledTags: ['transactionTags', 'securityTags', 'customTags'],
            };
        });
        setInputFiles(inputFiles);
        return inputFiles;
    };
    const rawInputData = useMemo(() => reAssignAdditionalInfo(data), [data]);
    const handlerUpdateFile = (files: any) => {
        setInputFiles(files);
        setIsDisable(isEqual(rawInputData, files));
    };
    const handleOnClose = () => {
        ga.event({
            action: 'Portfolio > Documents > Close Edit Document',
        });
        setShowModal(false);
        reAssignAdditionalInfo(data);
    };

    useEffect(() => {
        showModal ? onOpen() : onClose();
    }, [showModal]);

    const getFilesSummary = (inputFiles: any) => {
        if (inputFiles?.length) {
            if (
                inputFiles?.find(
                    (file: any) => file?.status === 'update-success'
                )
            ) {
                return `${
                    inputFiles?.filter(
                        (file: any) => file?.status === 'update-success'
                    )?.length
                } / ${inputFiles?.length} ${t(
                    'ui.mm.file-upload.updated-successfully'
                )}`;
            }
            if (
                inputFiles?.find(
                    (file: any) => file?.status === 'ready-to-update'
                )
            ) {
                return `${
                    inputFiles?.filter(
                        (file: any) => file?.status === 'ready-to-update'
                    )?.length
                } / ${inputFiles?.length} ${t(
                    'ui.mm.file-upload.ready-to-update'
                )}`;
            }
        }
        return '';
    };

    const handleFileUpdate = async () => {
        setIsLoading(true);
        let response = await updateTags(inputFiles);
        response = await updateFiles(response);
        setIsLoading(false);
        setInputFiles(response);
        setIsFinalState(true);
        setShowModal(false);
        refreshData && refreshData();
    };

    useEffect(() => {
        reAssignAdditionalInfo(data);
    }, [data]);
    return (
        <Modal
            isCentered
            closeOnOverlayClick={false}
            onEsc={() => {
                setShowModal(false);
                reAssignAdditionalInfo(data); //onEsc we're re-assigning the previous data of the modal
            }}
            isOpen={isOpen}
            size="6xl"
            onClose={onClose}>
            <ModalOverlay
                style={{
                    backdropFilter: 'blur(5px)',
                }}
            />
            <ModalContent>
                <ModalHeader
                    color={currentColors.brand['10']}
                    fontSize="20px"
                    fontWeight="bold"
                    padding="15px">
                    <Flex alignItems="center">
                        <Image
                            src={theme?.images?.headerLogo}
                            maxWidth="100%"
                            width="auto"
                            height={'35px'}
                            mr="17.5px"
                        />
                        <Text width="100%">
                            {t('ui.mm.tooltip.edit-file-details', {
                                count: inputFiles?.length,
                            })}
                        </Text>
                    </Flex>
                    <Divider
                        borderColor={currentColors.brand['700']}
                        width="100%"
                        mb="0px"
                    />
                </ModalHeader>
                {
                    <ModalCloseButton
                        as={() => (
                            <CanopyIconButton
                                background="transparent"
                                cursor="pointer"
                                border="none"
                                display="inline-flex"
                                onClick={handleOnClose}
                                color={currentColors.brand['500']}
                                width="24px"
                                height="24px"
                                aria-label="remove"
                                icon="ActClose"
                                variant="ghost"
                                position="absolute"
                                top="15px"
                                right="15px"
                                isDisabled={isLoading}
                            />
                        )}
                    />
                }
                <ModalBody
                    padding="0 15px"
                    maxHeight="60vh"
                    overflow="auto">
                    {inputFiles?.length ? (
                        <>
                            <Flex
                                color="whiteAlpha.500"
                                fontSize="13px"
                                justifyContent="end">
                                {getFilesSummary(inputFiles)}
                            </Flex>
                            <EditFileDetails
                                isFinalState={isFinalState}
                                inputFiles={inputFiles}
                                globalProps={globalProps}
                                userId={userId}
                                updateFiles={
                                    handlerUpdateFile
                                }></EditFileDetails>
                        </>
                    ) : null}
                </ModalBody>

                {inputFiles?.length ? (
                    <ModalFooter
                        flexDirection="column"
                        p="15px">
                        <Divider
                            mt="0"
                            mb="15px"
                        />
                        <HStack
                            justify="flex-end"
                            width="100%"
                            spacing="15px">
                            <CanopyButton
                                onClick={handleFileUpdate}
                                isDisabled={isDisable}
                                data-test="uploadButton"
                                width="120px"
                                height="30px"
                                isLoading={isLoading}
                                fontSize="sm">
                                {t('ui.mm.update')}
                            </CanopyButton>
                        </HStack>
                    </ModalFooter>
                ) : null}
            </ModalContent>
        </Modal>
    );
}

export default EditFileDetailsModal;
